import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { PageHeader } from '@/components/common/PageHeader'
import { TabelaAcao, ColunaTabelaAcao } from '@/components/common/TabelaAcao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { baixarCsv, sufixoData } from '@/utils/csv'
import { oportunidadeService } from '@/services/oportunidadeService'
import { clienteService } from '@/services/clienteService'
import { Oportunidade } from '@/types/oportunidade'
import { Cliente } from '@/types/cliente'
import { StatusReserva } from '@/types/statusReserva'
import { formatCNPJ } from '@/utils/formatters'
import { cnpjContemTermo } from '@/utils/cnpj'
import {
  FiltroConsultor,
  TODOS_CONSULTORES,
  consultoresDasReservas,
} from '@/components/common/FiltroConsultor'

interface OperacaoRow {
  id: string
  empresa: string
  cnpj: string
  consultorId: string | null
  /** Nome gravado na reserva (V49); "—" em reserva antiga sem nome conhecido. */
  consultor: string
  produto: string
  valor: number
  data: string
  diasDesdeFechamento: number
  status: 'Ativa' | 'Finalizada'
}

// Esta tela NÃO exibe comissão. Até 14/08/2026 mostrava `valor × 2,5%` — uma taxa fixa no
// código que não vinha de regra nenhuma e divergia do Financeiro, onde a comissão é
// lançada à mão pelo operador (decisão do time comercial em 04/08). A comissão de uma
// reserva se consulta no Financeiro ou no cartão de comissões da própria reserva.

/**
 * Status da fase Contratação / Operação (mais ENCERRADA, que pode ter tido contrato). A lista
 * vem do servidor por status (14/10/2026) em vez das 200 reservas mais recentes filtradas na
 * tela, que deixavam operações antigas de fora; o recorte final continua pelo estágio legado
 * CONTRATADA, que exclui a reserva encerrada sem contrato.
 */
export const STATUS_OPERACAO: StatusReserva[] = [
  'EM_CONTRATACAO',
  'CONTRATO_ASSINADO',
  'EM_OPERACIONALIZACAO',
  'ATIVO',
  'ENCERRADA',
]

const formatBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

interface Props {
  /**
   * Onda 2 / A04: renderizada como aba de "Operações e Clientes", que já traz PageHeader
   * e KPIs — o cabeçalho local sai.
   */
  embutida?: boolean
}

const OperacoesPage: FC<Props> = ({ embutida = false }) => {
  const navigate = useNavigate()
  const [oportunidades, setOportunidades] = useState<Oportunidade[]>([])
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [erroStatus, setErroStatus] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [consultorId, setConsultorId] = useState<string>(TODOS_CONSULTORES)

  const carregar = useCallback(async () => {
    setLoading(true)
    setErro(null)
    setErroStatus(null)
    try {
      const [pageOp, pageCli] = await Promise.all([
        oportunidadeService.filtrar(0, 500, { status: STATUS_OPERACAO.join(',') }),
        // Só complementa reservas antigas sem razão social/CNPJ; sem ele a aba continua.
        clienteService.listar(0, 200).catch(() => ({ content: [] as Cliente[] })),
      ])
      setOportunidades(pageOp.content ?? [])
      setClientes(pageCli.content ?? [])
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar operações')
      setErroStatus((e as { status?: number })?.status ?? null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const operacoes = useMemo<OperacaoRow[]>(() => {
    const clienteById = new Map(clientes.map((c) => [c.id, c]))
    const agora = Date.now()

    return oportunidades
      .filter((o) => String(o.estagio) === 'CONTRATADA')
      .map((o) => {
        const cliente = clienteById.get(o.clienteId as unknown as string)
        const valor = Number(o.valorEstimado ?? 0)
        const dataRaw = o.updatedAt ?? o.createdAt
        const dataObj = dataRaw ? new Date(dataRaw) : null
        const dias = dataObj ? Math.floor((agora - dataObj.getTime()) / (1000 * 60 * 60 * 24)) : 0
        const status: OperacaoRow['status'] = dias > 180 ? 'Finalizada' : 'Ativa'
        // Empresa e CNPJ da própria reserva (cadastro em 3 etapas); o cadastro de clientes,
        // que o wizard não alimenta, fica só para reservas antigas.
        const cnpj = o.cnpj || cliente?.cnpj
        return {
          id: o.id,
          empresa:
            o.razaoSocial || o.nomeFantasia || cliente?.razaoSocial || `Cliente ${String(o.clienteId).slice(0, 8)}…`,
          cnpj: cnpj ? formatCNPJ(cnpj) : '—',
          consultorId: o.usuarioResponsavel ?? null,
          consultor: o.consultorNome ?? '—',
          produto: o.tipoNecessidade ?? '—',
          valor,
          data: dataObj ? dataObj.toLocaleDateString('pt-BR') : '—',
          diasDesdeFechamento: dias,
          status,
        }
      })
      .sort((a, b) => a.diasDesdeFechamento - b.diasDesdeFechamento)
  }, [oportunidades, clientes])

  const opcoesConsultor = useMemo(
    () => consultoresDasReservas(oportunidades.filter((o) => String(o.estagio) === 'CONTRATADA')),
    [oportunidades],
  )

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return operacoes.filter(
      (o) =>
        (consultorId === TODOS_CONSULTORES || o.consultorId === consultorId) &&
        (o.empresa.toLowerCase().includes(q) ||
          cnpjContemTermo(o.cnpj, search) ||
          o.produto.toLowerCase().includes(q) ||
          o.consultor.toLowerCase().includes(q))
    )
  }, [operacoes, search, consultorId])

  const totalValor = filtered.reduce((acc, o) => acc + o.valor, 0)

  // Exporta o que está na tela — filtrado e no escopo do usuário (Fase 6, item 22).
  const exportarCsv = () =>
    baixarCsv(`operacoes-${sufixoData()}`,
      ['Empresa', 'CNPJ', 'Consultor', 'Produto', 'Valor', 'Data', 'Dias desde fechamento', 'Status'],
      filtered.map((o) => [o.empresa, o.cnpj, o.consultor, o.produto, o.valor, o.data, o.diasDesdeFechamento, o.status]))

  // Padrão do Figma (Onda 2): tabela com `Abrir →` na última coluna.
  const colunas: ColunaTabelaAcao<OperacaoRow>[] = [
    { titulo: 'Empresa', render: (o) => <strong>{o.empresa}</strong> },
    {
      titulo: 'CNPJ',
      render: (o) => (
        <Typography component="span" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
          {o.cnpj}
        </Typography>
      ),
    },
    { titulo: 'Consultor', render: (o) => o.consultor },
    { titulo: 'Produto', render: (o) => o.produto },
    {
      titulo: 'Valor',
      align: 'right',
      render: (o) => <span style={{ fontWeight: 600 }}>{formatBRL(o.valor)}</span>,
    },
    { titulo: 'Data', render: (o) => o.data },
    {
      titulo: 'Status',
      render: (o) => (
        <Chip label={o.status} size="small" color={o.status === 'Ativa' ? 'success' : 'default'} />
      ),
    },
  ]

  return (
    <Box>
      {!embutida && (
        <PageHeader
          title="Operações"
          subtitle="Acompanhe as operações contratadas (ativas e finalizadas)"
        />
      )}

      <Paper>
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Buscar por empresa, CNPJ, produto ou consultor…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            sx={{ flex: 1, maxWidth: 400 }}
          />
          <FiltroConsultor opcoes={opcoesConsultor} valor={consultorId} onChange={setConsultorId} />
          {!loading && !erro && (
            <Box sx={{ display: 'flex', gap: 2, ml: 'auto', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                <strong>{filtered.length}</strong> operações
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total: <strong>{formatBRL(totalValor)}</strong>
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={exportarCsv}
                disabled={filtered.length === 0}
              >
                Exportar CSV
              </Button>
            </Box>
          )}
        </Box>

        {erro ? (
          <EstadoDeLista
            estado={erroStatus === 403 ? 'semPermissao' : 'erro'}
            {...(erroStatus !== 403 && {
              mensagem: 'Não foi possível carregar as operações',
              detalhe: erro,
              acao: { label: 'Tentar novamente', onClick: () => void carregar() },
            })}
          />
        ) : loading ? (
          <EstadoDeLista estado="carregando" mensagem="Carregando operações…" />
        ) : operacoes.length === 0 ? (
          <EstadoDeLista
            estado="vazio"
            mensagem="Nenhuma operação contratada"
            detalhe='Quando uma oportunidade chegar ao estágio "Contratada", aparece aqui.'
          />
        ) : filtered.length === 0 ? (
          <EstadoDeLista
            estado="vazio"
            mensagem="Nenhuma operação com esses filtros"
            detalhe="Ajuste a busca ou o consultor."
          />
        ) : (
          <TabelaAcao
            aria-label="Operações contratadas"
            colunas={colunas}
            linhas={filtered}
            chaveDaLinha={(o) => o.id}
            onAbrir={(o) => navigate(`/oportunidades/${o.id}`)}
          />
        )}
      </Paper>
    </Box>
  )
}

export default OperacoesPage
